import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { sessionLines, sessionNetIncome, isEstimatedIncome, sessionIncomeAmount, discountedNetIncome } from "@/lib/sessionPnl";
import { bankFeeTotal, bookingBankFee, calculateSessionPnl, costLineTotal, effectiveBankFee, isCardPayment, isCountedIncome, leadActivityIncome } from "@/lib/sessionPnl";
import { calculateItemTotalCost } from "@/lib/itineraryCostUtils";
import { toZAR } from "@/lib/budgetActualUtils";
import { useExchangeRates } from "@/hooks/useExchangeRates";

export function fmtZAR(n: number) {
  return new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n || 0);
}

interface SupplierRef {
  id: string; // booking_suppliers.id or lead_item_suppliers.id
  name: string;
  category?: string | null;
  supplier_id?: string | null;
}

type Props =
  | {
      mode: "instant";
      scopeKey: string;
      brandId?: string | null;
      sessionDate?: string | null;
      bookings: any[];
      headcount: number;
      suppliers: SupplierRef[];
    }
  | {
      mode: "lead";
      leadId: string;
      itemId: string;
      suppliers: SupplierRef[];
    };

export function SessionFinanceTab(props: Props) {
  const qc = useQueryClient();
  const { data: rates } = useExchangeRates();
  const scopeKey = props.mode === "instant" ? props.scopeKey : `lead:${props.leadId}:${props.itemId}`;
  const [showBookings, setShowBookings] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, { unit_amount?: string; label?: string }>>({});
  const [showFees, setShowFees] = useState(false);
  const [feeDraft, setFeeDraft] = useState<string | null>(null);

  const { data: costs = [], isLoading } = useQuery({
    queryKey: ["session-costs", scopeKey],
    queryFn: async () => {
      const { data, error } = await supabase.from("session_costs").select("*").eq("scope_key", scopeKey).order("created_at");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: statusRow } = useQuery({
    queryKey: ["session-finance-status", scopeKey],
    queryFn: async () => {
      const { data } = await supabase.from("session_finance_status").select("*").eq("scope_key", scopeKey).maybeSingle();
      return data;
    },
  });

  // ---------- Income ----------
  const bookingIds = props.mode === "instant" ? props.bookings.map((b) => b.id) : [];
  const { data: incomeRows = [] } = useQuery({
    queryKey: ["session-income", scopeKey, bookingIds.join(",")],
    enabled: props.mode === "instant" && bookingIds.length > 0,
    queryFn: async () => {
      const p = props as Extract<Props, { mode: "instant" }>;
      const [{ data: meta }, { data: items }, { data: extras }] = await Promise.all([
        supabase.from("coffeebeans_bookings").select("id, is_test, payment_status, status, total_amount, amount_paid, payment_method, yoco_checkout_id, agent_code_id, commission_already_deducted, agent_codes(agent_type, commission_percentage)").in("id", bookingIds),
        supabase.from("booking_items").select("booking_id, subtotal, guest_count, experience_name, experience_date, experience_time, status").in("booking_id", bookingIds),
        supabase.from("booking_extras").select("*").in("booking_id", bookingIds).eq("status", "active"),
      ]);
      return p.bookings.filter((b: any) => {
        const st = (meta ?? []).find((x: any) => x.id === b.id) as any;
        return (st?.status ?? b.status) !== "cancelled";
      }).map((b) => {
        const m = (meta ?? []).find((x: any) => x.id === b.id) as any;
        const lines = sessionLines(items ?? [], b);
        const rate = m?.agent_codes?.agent_type === "AGENT" ? Number(m.agent_codes.commission_percentage) || 0 : 0;
        const bExtras = (extras ?? []).filter((e: any) => e.booking_id === b.id) as any;
        const allLines = (items ?? []).filter((i: any) => i.booking_id === b.id && i.status !== "cancelled");
        const gross = lines.length ? sessionNetIncome({ lines, commissionRate: 0, commissionAlreadyDeducted: true, extras: bExtras }) : 0;
        const discounted = lines.length ? discountedNetIncome({ calculatedGross: gross, savedTotal: m?.total_amount, wholeBookingInSession: allLines.length === lines.length }) : null;
        const calculated = discounted ?? (lines.length
          ? sessionNetIncome({ lines, commissionRate: rate, commissionAlreadyDeducted: !!m?.commission_already_deducted, extras: bExtras })
          : Number(m?.amount_paid ?? m?.total_amount ?? b.total_amount ?? 0));
        const { amount, paidDiffers } = sessionIncomeAmount({ calculated, paymentStatus: m?.payment_status ?? b.payment_status, amountPaid: m?.amount_paid, wholeBookingInSession: allLines.length === lines.length });
        const pax = lines.length ? lines.reduce((s: number, i: any) => s + Number(i.guest_count || 0), 0) : b.guest_count;
        return {
          id: b.id,
          name: b.guest_name,
          number: b.booking_number,
          pax,
          amount,
          paidDiffers,
          status: m?.status ?? b.status,
          payment_status: m?.payment_status ?? b.payment_status,
          is_test: m?.is_test ?? false,
          payment_method: m?.payment_method ?? null,
          yoco_checkout_id: m?.yoco_checkout_id ?? null,
        };
      });
    },
  });

  const { data: leadData } = useQuery({
    queryKey: ["lead-activity-income", props.mode === "lead" ? props.leadId : null],
    enabled: props.mode === "lead",
    queryFn: async () => {
      const p = props as Extract<Props, { mode: "lead" }>;
      return loadLeadFinance(p.leadId);
    },
  });

  let income = 0;
  let headcount = 0;
  let leadActuals: any[] = [];
  if (props.mode === "instant") {
    const basePax = props.bookings.reduce((s2: number, b: any) => s2 + Number(b.guest_count || 0), 0);
    const linePax = incomeRows.filter((r: any) => r.status !== "cancelled" && !r.is_test).reduce((s2: number, r: any) => s2 + Number(r.pax || 0), 0);
    headcount = props.headcount - basePax + (incomeRows.length ? linePax : basePax);
  } else if (leadData) {
    const item = leadData.items.find((i: any) => i.id === props.itemId);
    if (item) {
      income = leadActivityIncome(calculateItemTotalCost(item), leadData.allItemsCost, toZAR(leadData.quoteTotalZar, leadData.currency, rates));
      headcount = item.quantity || 1;
    }
    leadActuals = leadData.actuals.filter((a: any) => a.itinerary_item_id === props.itemId);
  }

  const feeRow = (costs as any[]).find((c) => c.category === "bank_fee");
  const lineCosts = (costs as any[]).filter((c) => c.category !== "bank_fee");
  const autoFee = props.mode === "instant" ? bankFeeTotal(incomeRows) : 0;
  const bankFee = props.mode === "instant" ? effectiveBankFee(incomeRows, feeRow ? Number(feeRow.unit_amount) : null) : 0;

  const actualsTotal = leadActuals.reduce((s, a) => s + toZAR(Number(a.amount), a.currency, rates), 0);
  const pnl = calculateSessionPnl(
    props.mode === "instant" ? incomeRows : [],
    lineCosts.map((c: any) => ({ ...c, unit_amount: drafts[c.id]?.unit_amount ?? c.unit_amount })),
    headcount,
    (statusRow?.status ?? "estimated") === "estimated",
  );
  if (props.mode === "lead") {
    pnl.income = income;
    pnl.totalCosts += actualsTotal;
  }
  pnl.totalCosts += bankFee;
  pnl.profit = pnl.income - pnl.totalCosts;
  pnl.margin = pnl.income ? (pnl.profit / pnl.income) * 100 : 0;

  const unpricedSuppliers = props.suppliers.filter(
    (s) => !costs.some((c: any) => c.booking_supplier_id === s.id || c.lead_item_supplier_id === s.id),
  );

  const invalidate = () => qc.invalidateQueries({ queryKey: ["session-costs", scopeKey] }).then(() =>
    qc.invalidateQueries({ queryKey: ["lead-finance-summary"] }),
  );

  const baseRow = () =>
    props.mode === "instant"
      ? { scope_key: scopeKey, brand_id: props.brandId ?? null, session_date: props.sessionDate ?? null }
      : { scope_key: scopeKey, lead_id: props.leadId, itinerary_item_id: props.itemId };

  const addLine = async (supplier?: SupplierRef) => {
    const row: any = {
      ...baseRow(),
      label: supplier ? supplier.name : "",
      category: supplier ? supplier.category ?? null : "custom",
      supplier_id: supplier?.supplier_id ?? null,
      cost_basis: "flat",
      unit_amount: 0,
    };
    if (supplier) row[props.mode === "instant" ? "booking_supplier_id" : "lead_item_supplier_id"] = supplier.id;
    const { error } = await supabase.from("session_costs").insert(row);
    if (error) return toast.error(error.message);
    invalidate();
  };

  const updateLine = async (id: string, patch: any) => {
    const { error } = await supabase.from("session_costs").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) return toast.error(error.message);
    setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
    invalidate();
  };

  const removeLine = async (id: string) => {
    const { error } = await supabase.from("session_costs").delete().eq("id", id);
    if (error) return toast.error(error.message);
    invalidate();
  };

  const saveFeeOverride = async (raw: string) => {
    setFeeDraft(null);
    if (raw.trim() === "") { if (feeRow) await removeLine(feeRow.id); return; }
    const value = Number(raw);
    if (feeRow) { if (value !== Number(feeRow.unit_amount)) await updateLine(feeRow.id, { unit_amount: value }); return; }
    const { error } = await supabase.from("session_costs").insert({
      ...baseRow(), label: "Bank fees", category: "bank_fee", cost_basis: "flat", unit_amount: value,
    } as any);
    if (error) return toast.error(error.message);
    invalidate();
  };

  const setStatus = async (status: string) => {
    const { error } = await supabase.from("session_finance_status").upsert({ scope_key: scopeKey, status, updated_at: new Date().toISOString() });
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["session-finance-status", scopeKey] });
  };

  if (isLoading) return <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-4 overflow-y-auto pr-1">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Income</h4>
        <Select value={statusRow?.status ?? "estimated"} onValueChange={setStatus}>
          <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="estimated">Estimated</SelectItem>
            <SelectItem value="final">Final</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {props.mode === "instant" ? (
        <div className="rounded-md border p-3">
          <button type="button" className="flex w-full items-center justify-between text-sm" onClick={() => setShowBookings((v) => !v)}>
            <span className="flex items-center gap-1">
              {showBookings ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {headcount} guests across {incomeRows.filter((r) => r.status !== "cancelled" && !r.is_test).length} bookings
            </span>
            <strong>{fmtZAR(pnl.income)}</strong>
          </button>
          {showBookings && (
            <div className="mt-2 space-y-1 border-l-2 pl-3 text-xs">
              {incomeRows.map((r) => (
                <div key={r.id} className="flex justify-between gap-2">
                  <span className="truncate">
                    {r.name} · {r.pax} pax {r.number ? `· ${r.number}` : ""}
                    {r.is_test && <Badge variant="secondary" className="ml-1 text-[10px]">Test</Badge>}
                    {(r as any).paidDiffers && (
                      <Badge variant="outline" className="ml-1 text-[10px]">Paid amount differs from calculated</Badge>
                    )}
                    {r.payment_status !== "paid" && (
                      <Badge variant="outline" className="ml-1 text-[10px]">
                        {(statusRow?.status ?? "estimated") === "estimated" ? "Invoiced, not yet paid" : "Not paid — excluded"}
                      </Badge>
                    )}
                  </span>
                  <span>{fmtZAR(r.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-md border p-3 text-sm flex justify-between">
          <span>Activity share of accepted quote{!leadData?.quoteTotalZar && " (no accepted quote yet)"}</span>
          <strong>{fmtZAR(pnl.income)}</strong>
        </div>
      )}

      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Costs</h4>
      <div className="space-y-2">
        {lineCosts.map((c: any) => {
          const unit = drafts[c.id]?.unit_amount ?? String(c.unit_amount ?? "");
          const lineTotal = costLineTotal({ cost_basis: c.cost_basis, unit_amount: unit }, headcount);
          const isSupplier = !!(c.booking_supplier_id || c.lead_item_supplier_id);
          return (
            <div key={c.id} className="flex flex-wrap items-center gap-2">
              {isSupplier ? (
                <span className="min-w-[140px] flex-1 text-sm">
                  {c.label} {c.category && <span className="text-xs text-muted-foreground">({c.category})</span>}
                </span>
              ) : (
                <Input
                  className="h-8 min-w-[140px] flex-1"
                  placeholder="e.g. Mineral water"
                  value={drafts[c.id]?.label ?? c.label}
                  onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: { ...d[c.id], label: e.target.value } }))}
                  onBlur={(e) => e.target.value !== c.label && updateLine(c.id, { label: e.target.value })}
                />
              )}
              <Input
                type="number"
                min="0"
                step="0.01"
                className="h-8 w-24"
                value={unit}
                onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: { ...d[c.id], unit_amount: e.target.value } }))}
                onBlur={(e) => Number(e.target.value || 0) !== Number(c.unit_amount) && updateLine(c.id, { unit_amount: Number(e.target.value || 0) })}
              />
              <Select value={c.cost_basis} onValueChange={(v) => updateLine(c.id, { cost_basis: v })}>
                <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Per session</SelectItem>
                  <SelectItem value="per_person">Per person</SelectItem>
                </SelectContent>
              </Select>
              <span className="w-24 text-right text-sm">
                {c.cost_basis === "per_person" && <span className="text-xs text-muted-foreground">×{headcount} </span>}
                {fmtZAR(lineTotal)}
              </span>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeLine(c.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        })}

        {leadActuals.map((a) => (
          <div key={a.id} className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{a.supplier_name || "Logged payment"} · from Budget vs Actual</span>
            <span>{fmtZAR(toZAR(Number(a.amount), a.currency, rates))}</span>
          </div>
        ))}

        {unpricedSuppliers.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-md border border-dashed p-2 text-sm">
            <span>{s.name} {s.category && <span className="text-xs text-muted-foreground">({s.category})</span>}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addLine(s)}>
              <Plus className="mr-1 h-3 w-3" /> Add cost
            </Button>
          </div>
        ))}

        {props.mode === "instant" && (
          <div className="rounded-md border p-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="flex flex-1 items-center gap-1 text-left" onClick={() => setShowFees((v) => !v)}>
                {showFees ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                Bank fees (card / online, 3.5%)
                {feeRow && <Badge variant="outline" className="ml-1 text-[10px]">Overridden</Badge>}
              </button>
              <Input
                type="number"
                min="0"
                step="0.01"
                className="h-8 w-24"
                placeholder={autoFee.toFixed(2)}
                value={feeDraft ?? (feeRow ? String(feeRow.unit_amount ?? "") : "")}
                onChange={(e) => setFeeDraft(e.target.value)}
                onBlur={(e) => saveFeeOverride(e.target.value)}
              />
              <span className="w-24 text-right">{fmtZAR(bankFee)}</span>
              {feeRow ? (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setFeeDraft(null); removeLine(feeRow.id); }}>
                  Reset to 3.5%
                </Button>
              ) : <span className="w-[88px]" />}
            </div>
            {feeRow && (
              <p className="mt-1 text-xs text-muted-foreground">
                Override · auto would be {fmtZAR(autoFee)}{feeRow.updated_at ? ` · changed ${new Date(feeRow.updated_at).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" })}` : ""}
              </p>
            )}
            {showFees && (
              <div className="mt-2 space-y-1 border-l-2 pl-3 text-xs">
                {incomeRows.filter(isCountedIncome).map((r) => (
                  <div key={r.id} className="flex justify-between gap-2">
                    <span className="truncate">
                      {r.name} · {fmtZAR(r.amount)} · {isCardPayment(r) ? (r.yoco_checkout_id ? "Yoco online" : String(r.payment_method).replace(/_/g, " ")) : `${(r.payment_method || "other").replace(/_/g, " ")} — no fee`}
                    </span>
                    <span>{fmtZAR(bookingBankFee(r))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Button size="sm" variant="outline" onClick={() => addLine()}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add cost line
        </Button>
      </div>

      <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
        <div className="flex justify-between"><span>Total income</span><strong>{fmtZAR(pnl.income)}</strong></div>
        <div className="flex justify-between"><span>Total costs</span><strong>{fmtZAR(pnl.totalCosts)}</strong></div>
        <div className="flex justify-between border-t pt-1 text-base">
          <span>Profit</span>
          <strong className={pnl.profit >= 0 ? "text-green-600" : "text-destructive"}>
            {fmtZAR(pnl.profit)} <span className="text-xs font-normal text-muted-foreground">Margin {pnl.margin.toFixed(1)}%</span>
          </strong>
        </div>
      </div>
    </div>
  );
}

/** Shared loader: lead items, accepted quote total (ZAR), actuals and session costs. */
export async function loadLeadFinance(leadId: string) {
  const [{ data: items }, { data: quotes }, { data: actuals }, { data: costs }, { data: lead }] = await Promise.all([
    supabase.from("itinerary_items").select("*").eq("lead_id", leadId).or("include_in_quote.is.null,include_in_quote.eq.true").order("date").order("sort_order"),
    supabase.from("quotations").select("total_amount, status, created_at").eq("lead_id", leadId).eq("status", "accepted").order("created_at", { ascending: false }).limit(1),
    supabase.from("itinerary_item_actuals").select("*").eq("lead_id", leadId),
    supabase.from("session_costs").select("*").eq("lead_id", leadId),
    supabase.from("leads").select("currency").eq("id", leadId).maybeSingle(),
  ]);
  const list = (items ?? []) as any[];
  const allItemsCost = list.reduce((s, i) => s + calculateItemTotalCost(i), 0);
  return {
    items: list,
    allItemsCost,
    quoteTotalZar: Number(quotes?.[0]?.total_amount || 0),
    currency: (lead as any)?.currency || "ZAR",
    actuals: actuals ?? [],
    costs: costs ?? [],
  };
}
